import { useState } from 'react';
import { useLoaderData, useNavigate } from 'react-router';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  toast,
} from '@repo-manager/ui';

import { getApi } from '../lib/api';
import { getActiveRepoId, useRepos } from '../lib/repo-context';
import { getSupabase } from '../lib/supabase';

interface Candidate {
  id: string;
  title: string;
  effort: string;
  status: 'draft' | 'ready';
  epic_id: string;
  epic_title: string | null;
}

interface LoaderData {
  repoId: string | null;
  candidates: Candidate[];
}

export async function newSprintLoader(): Promise<LoaderData> {
  const supabase = getSupabase();
  const repoId = await getActiveRepoId(supabase);
  if (!repoId) return { repoId: null, candidates: [] };
  // Pull both `draft` and `ready` stories — the agent writes new
  // stories as `draft` and we don't yet have a separate review surface,
  // so the sprint planner is the natural place to triage them. Picking
  // a draft promotes it to `ready` as part of the sprint confirm step
  // (see `submit()` below). Stories already on a sprint or finished are
  // filtered out by status, not by a `sprint_stories` exclusion — that
  // join is overkill for the few-rows-per-repo scale we have.
  const { data } = await supabase
    .from('stories')
    .select('id, title, effort, status, epic_id, epics(title)')
    .eq('repo_id', repoId)
    .in('status', ['draft', 'ready'])
    .order('created_at', { ascending: true });
  const candidates: Candidate[] = (data ?? []).map((r) => {
    const epic = (r as { epics?: { title: string } | null }).epics ?? null;
    return {
      id: r.id as string,
      title: r.title as string,
      effort: r.effort as string,
      status: r.status as 'draft' | 'ready',
      epic_id: r.epic_id as string,
      epic_title: epic?.title ?? null,
    };
  });
  return { repoId, candidates };
}

export async function newSprintAction(): Promise<null> {
  return null;
}

export function NewSprintPage() {
  const { repoId, candidates } = useLoaderData() as LoaderData;
  const { activeRepo } = useRepos();
  const scope = activeRepo && activeRepo.id === repoId ? activeRepo : null;
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '',
    goal: '',
    start_date: '',
    end_date: '',
  });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function submit() {
    if (!repoId) {
      toast.error('No repo connected.');
      return;
    }
    if (picked.size === 0) {
      toast.error('Pick at least one story.');
      return;
    }
    if (!form.title || !form.goal) {
      toast.error('Title and goal are required.');
      return;
    }
    setSubmitting(true);
    const supabase = getSupabase();

    const { data: sprint, error } = await supabase
      .from('sprints')
      .insert({
        repo_id: repoId,
        title: form.title,
        goal: form.goal,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        status: 'planning',
      })
      .select('id')
      .single();
    if (error || !sprint) {
      setSubmitting(false);
      toast.error('Failed to create sprint', { description: error?.message });
      return;
    }

    const sprintStoryRows = [...picked].map((story_id) => ({ sprint_id: sprint.id, story_id }));
    const { error: ssErr } = await supabase.from('sprint_stories').insert(sprintStoryRows);
    if (ssErr) {
      setSubmitting(false);
      toast.error('Sprint created but story attachment failed', { description: ssErr.message });
      navigate(`/sprints/${sprint.id}`);
      return;
    }

    // Promote any picked drafts to `ready`. Putting a story on a sprint
    // implies the operator has reviewed it — the explicit `draft → ready`
    // gate exists in the state machine, we just haven't built a separate
    // review surface for it. Skip if all picks were already ready.
    const draftIds = candidates
      .filter((c) => picked.has(c.id) && c.status === 'draft')
      .map((c) => c.id);
    if (draftIds.length > 0) {
      const { error: promoteErr } = await supabase
        .from('stories')
        .update({ status: 'ready' })
        .in('id', draftIds);
      if (promoteErr) {
        // Non-fatal — the sprint is created + attached; the operator
        // can promote later. Surface it so they know.
        toast.warning('Sprint created, but a story promotion failed', {
          description: promoteErr.message,
        });
      }
    }

    try {
      await getApi().triggerConfirmSprint({ sprint_id: sprint.id, repo_id: repoId });
      await supabase.from('sprints').update({ status: 'active' }).eq('id', sprint.id);
      toast.success('Sprint confirmed.');
    } catch (e) {
      toast.warning('Sprint created but trigger failed', {
        description: e instanceof Error ? e.message : String(e),
      });
    }
    setSubmitting(false);
    navigate(`/sprints/${sprint.id}`);
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Plan sprint</h1>
        {scope ? (
          <p className="text-sm text-muted-foreground">
            for <code className="font-mono">{scope.owner}/{scope.name}</code>
          </p>
        ) : !repoId ? (
          <p className="text-sm text-muted-foreground">
            No repo selected — pick one from the header dropdown before planning.
          </p>
        ) : null}
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Window + goal</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="goal">Goal</Label>
            <Textarea
              id="goal"
              rows={3}
              value={form.goal}
              onChange={(e) => setForm((f) => ({ ...f, goal: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="start_date">Start</Label>
              <Input
                id="start_date"
                type="date"
                value={form.start_date}
                onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_date">End</Label>
              <Input
                id="end_date"
                type="date"
                value={form.end_date}
                onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pick stories</CardTitle>
          <CardDescription>
            Draft + ready stories for this repo. Picking a draft also flips
            it to <code>ready</code> when the sprint is confirmed.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {candidates.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title="No candidate stories"
                description="No draft or ready stories for this repo yet. Approve an epic and wait a cycle — the manager DO writes stories under it via supabase_propose_stories."
              />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Pick</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Epic</TableHead>
                  <TableHead>Effort</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {candidates.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>
                      <input
                        type="checkbox"
                        aria-label={`Include ${c.title}`}
                        checked={picked.has(c.id)}
                        onChange={() => toggle(c.id)}
                      />
                    </TableCell>
                    <TableCell>{c.title}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {c.epic_title ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{c.effort}</TableCell>
                    <TableCell>
                      <Badge variant={c.status === 'ready' ? 'secondary' : 'outline'}>
                        {c.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Button onClick={() => void submit()} disabled={submitting || picked.size === 0}>
          {submitting ? 'Confirming…' : 'Confirm sprint'}
        </Button>
        <Button variant="outline" onClick={() => navigate(-1)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
